/**
 * 表格键盘导航（03-UIUX.md §6.4「面向急躁高手」）。
 *
 * 只实现本迭代能完整交付的子集：`j`/`k`/`↑`/`↓` 移动行焦点、`Enter` 打开、`/` 聚焦搜索。
 * `x` 选中、`Shift+x` 范围选中依赖批量操作栏，那部分未实现，**因此不做半截的按键**——
 * 一个按下去没反应的快捷键比没有快捷键更消耗信任。
 *
 * 关键防错：在输入框里按 `j` 必须是打字，不是跳行。
 */
import { useEffect } from 'react';
import type { RefObject } from 'react';

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export interface TableKeyboardOptions {
  /** 表格容器，用于查找 `[data-license-row]` 之类的可聚焦行 */
  readonly containerRef: RefObject<HTMLElement | null>;
  readonly searchRef: RefObject<HTMLInputElement | null>;
  readonly rowSelector: string;
  /** Enter 在当前行上触发；index 为行在当前页中的序号 */
  readonly onActivate: (index: number) => void;
  readonly enabled: boolean;
}

export function useTableKeyboard({
  containerRef,
  searchRef,
  rowSelector,
  onActivate,
  enabled,
}: TableKeyboardOptions): void {
  useEffect(() => {
    if (!enabled) return undefined;

    const handler = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === '/' && !isTypingTarget(event.target)) {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }

      if (isTypingTarget(event.target)) return;

      const container = containerRef.current;
      if (container === null) return;
      const rows = Array.from(container.querySelectorAll<HTMLElement>(rowSelector));
      if (rows.length === 0) return;

      const currentIndex = rows.findIndex(
        (row) => row === document.activeElement || row.contains(document.activeElement),
      );

      if (event.key === 'j' || event.key === 'ArrowDown') {
        event.preventDefault();
        const next = currentIndex < 0 ? 0 : Math.min(currentIndex + 1, rows.length - 1);
        rows[next]?.focus();
        return;
      }

      if (event.key === 'k' || event.key === 'ArrowUp') {
        event.preventDefault();
        const next = currentIndex <= 0 ? 0 : currentIndex - 1;
        rows[next]?.focus();
        return;
      }

      if (event.key === 'Enter' && currentIndex >= 0) {
        event.preventDefault();
        onActivate(currentIndex);
      }
    };

    globalThis.addEventListener('keydown', handler);
    return () => globalThis.removeEventListener('keydown', handler);
  }, [containerRef, searchRef, rowSelector, onActivate, enabled]);
}
